import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";

import { AuthProvider } from "@/context/AuthContext";
import AcceptanceGate from "@/components/AcceptanceGate";
import { Toaster } from "@/components/ui/sonner";
import { UpgradeModalHost } from "@/components/platform/UpgradeModalHost";
import { captureFirstTouchAttribution } from "@/lib/attribution";
import { bootstrapConsent } from "@/lib/consent";
import CookieBanner from "@/components/CookieBanner";
import AuthCallback from "@/pages/AuthCallback";
import InterviewRoom from "@/pages/InterviewRoom";
import { RouteScrollReset } from "@/components/RouteScrollReset";

// Per-track route trees — see /src/routes/*.jsx
import { publicRoutes } from "@/routes/publicRoutes";
import { adminRoutes } from "@/routes/adminRoutes";
import { employerRoutes } from "@/routes/employerRoutes";
import { teamRoutes } from "@/routes/teamRoutes";
import { talentRoutes } from "@/routes/talentRoutes";

/**
 * Top-level router. Each track (public / admin / employer / team / recruiter
 * / talent) owns its own route tree in /src/routes to keep this file lean.
 */
function AppRouter() {
    const location = useLocation();
    // Google OAuth handoff arrives as a URL hash — resolve it synchronously
    // during render before any downstream route runs.
    if (location.hash?.includes("session_id=")) {
        return <AuthCallback />;
    }
    return (
        <Routes>
            {/* Full-screen interview room — no marketing shell, no sidebar */}
            <Route path="/interview/:bookingId" element={<InterviewRoom />} />
            {publicRoutes()}
            {adminRoutes()}
            {employerRoutes()}
            {teamRoutes()}
            {talentRoutes()}
            {/* Legacy /recruiter/* URLs — recruiters are now an employer
                permission, not a separate track. Redirect any deep-links. */}
            <Route path="/recruiter/*" element={<Navigate to="/employer/dashboard" replace />} />
        </Routes>
    );
}

function App() {
    // Stash first-touch UTM + referrer on the very first render — never
    // overwritten on subsequent visits (matches server-side policy).
    if (typeof window !== "undefined") {
        captureFirstTouchAttribution();
        // Reconcile analytics consent BEFORE any PostHog capture() calls fire.
        // Honors GPC + stored decision. Only loads PostHog if the visitor
        // has not rejected.
        bootstrapConsent();
    }
    // Site-wide verification meta tags — set REACT_APP_GOOGLE_SITE_VERIFICATION
    // and REACT_APP_BING_SITE_VERIFICATION after you claim the property in
    // Search Console / Bing Webmaster Tools. Nothing renders when the env
    // var is unset, so this is safe to ship even before you have the tokens.
    const googleVerify = process.env.REACT_APP_GOOGLE_SITE_VERIFICATION;
    const bingVerify   = process.env.REACT_APP_BING_SITE_VERIFICATION;
    return (
        <>
            <Helmet>
                <meta name="theme-color" content="#0F1B2E" />
                {googleVerify ? <meta name="google-site-verification" content={googleVerify} /> : null}
                {bingVerify ? <meta name="msvalidate.01" content={bingVerify} /> : null}
            </Helmet>
            <BrowserRouter>
                <RouteScrollReset />
                <AuthProvider>
                    <AppRouter />
                    <AcceptanceGate />
                    <UpgradeModalHost />
                    <CookieBanner />
                    <Toaster position="top-right" richColors />
                </AuthProvider>
            </BrowserRouter>
        </>
    );
}

export default App;
